import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom, Observable } from 'rxjs';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';

import {
  MsdAiResultSchema,
  MsdAiResultUtil,
} from '../../../core/domain/msd-ai-result';
import { GenericException } from '../../../core/logic/exception';
import { UserId } from '../../auth/domain/user';
import { PatientId } from '../../patient/domain/patient';
import { Diary } from '../domain/diary/diary';
import { DiaryNotFoundException } from '../domain/diary/diary_exceptions';
import { DiaryTracker } from '../domain/diary/diary_tracker';
import { Picture } from '../domain/diary/picture';
import { TrackerType } from '../domain/tracker/tracker_type';
import { DiaryTypeRepository, DiaryTypeSchema } from './diary_type_repository';

export interface DiarySchema {
  name: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  archivedAt: Timestamp | null;
  type: string | null;
  diaryType: DiaryTypeSchema | null;
  pictures: PictureSchema[];
  trackers: TrackerSchema[];
  comment: string | null;
  isSharedComment: boolean | null;
  patientUserId: string | null;
  timeOffset: number | null;
  msdAiResults: MsdAiResultSchema[];
}

interface PictureSchema {
  url: string;
  path: string;
  disabled?: boolean;
}

interface TrackerSchema {
  name: string;
  min: number;
  max: number;
  value: number;
  type: TrackerType;
  steps: number;
}

@Injectable()
export class DiaryRepository {
  private readonly generateDiaryPdfFromCloud: (
    data: unknown,
  ) => Observable<string>;

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
    private storage: AngularFireStorage,
  ) {
    this.generateDiaryPdfFromCloud = this.functions.httpsCallable<
      unknown,
      string
    >('diary-generateDiaryPdf');
  }

  private collection(patientId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('patients')
      .doc(patientId)
      .collection<DiarySchema>('diaries', queryFn);
  }

  private toSchema(diary: Diary): DiarySchema {
    return <DiarySchema>{
      name: diary.name ?? null,
      createdAt:
        diary.createdAt !== undefined
          ? Timestamp.fromDate(diary.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      timeOffset: diary.timeOffset ?? null,
      patientUserId: diary.patientUserId
        ? diary.patientUserId.id.toString()
        : null,
      type: diary.type ?? null,
      diaryType: diary.diaryType
        ? DiaryTypeRepository.toSchema(diary.diaryType)
        : null,
      pictures: diary.pictures.map(
        (p) =>
          <PictureSchema>{
            url: p.url,
            path: p.path,
            disabled: p.disabled ?? false,
          },
      ),
      archivedAt:
        diary.archivedAt !== undefined
          ? Timestamp.fromDate(diary.archivedAt)
          : null,
      trackers: diary.trackers.map(
        (t) =>
          <TrackerSchema>{
            name: t.name,
            min: t.min,
            max: t.max,
            value: t.value,
            type: t.type,
            steps: t.steps ?? null,
          },
      ),
      comment: diary.comment ?? null,
      isSharedComment: diary.isSharedComment ?? null,
      msdAiResults: MsdAiResultUtil.toSchemas(diary.msdAiResults),
    };
  }

  private fromSchema(
    schema: DiarySchema,
    patientId: string,
    id: string,
  ): Diary {
    return Diary.create(
      {
        patientId: PatientId.create(new UniqueEntityID(patientId)),
        patientUserId: schema.patientUserId
          ? UserId.create(new UniqueEntityID(schema.patientUserId))
          : undefined,
        name: schema.name ?? undefined,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt.toDate(),
        timeOffset: schema.timeOffset ?? undefined,
        type: schema.type ?? undefined,
        diaryType: schema.diaryType
          ? DiaryTypeRepository.fromSchema(schema.diaryType)
          : undefined,
        pictures:
          schema.pictures?.map((p) =>
            Picture.create({
              url: p.url,
              path: p.path,
              disabled: p.disabled ?? false,
            }),
          ) ?? [],
        archivedAt: schema.archivedAt?.toDate() ?? undefined,
        trackers:
          schema.trackers?.map((t) =>
            DiaryTracker.create({
              name: t.name,
              min: t.min ?? 0,
              max: t.max ?? 0,
              value: t.value ?? 0,
              type: t.type,
              steps: t.steps,
            }),
          ) ?? [],
        comment: schema.comment ?? undefined,
        isSharedComment: schema.isSharedComment ?? false,
        msdAiResults: MsdAiResultUtil.fromSchemas(schema.msdAiResults),
      },
      new UniqueEntityID(id),
    );
  }

  async load(patientId: string, diaryId: string): Promise<Diary> {
    const snap = await firstValueFrom(
      this.collection(patientId).doc(diaryId).get(),
    );
    if (!snap.exists || snap.data == null) {
      throw new DiaryNotFoundException();
    }

    return this.fromSchema(snap.data() as DiarySchema, patientId, snap.id);
  }

  async findDiaries(
    patientId: string,
    start: Date,
    end: Date,
  ): Promise<Diary[]> {
    const snap = await firstValueFrom(
      this.collection(patientId, (ref) =>
        ref
          .where('createdAt', '>=', start)
          .where('createdAt', '<=', end)
          .where('archivedAt', '==', null)
          .orderBy('createdAt'),
      ).get(),
    );

    return snap.docs.map((doc) =>
      this.fromSchema(doc.data(), patientId, doc.id),
    );
  }

  async create(diary: Diary): Promise<Diary> {
    const schema = this.toSchema(diary);
    const ref = await this.collection(diary.patientId.id.toString()).add(
      schema,
    );

    return this.fromSchema(schema, diary.patientId.id.toString(), ref.id);
  }

  async save(diary: Diary, diaryId?: string): Promise<Diary> {
    const schema = this.toSchema(diary);
    await this.collection(diary.patientId.id.toString())
      .doc(diaryId ? diaryId : diary.diaryId.id.toString())
      .set(schema);
    return this.fromSchema(
      schema,
      diary.patientId.id.toString(),
      diaryId ? diaryId : diary.diaryId.id.toString(),
    );
  }

  async generatePdf(
    patientId: string,
    date: Date,
    timeOffset: number,
    timeZone: string,
    withEncouragements: boolean,
    withImages: boolean,
  ): Promise<string> {
    try {
      const result = await firstValueFrom(
        this.generateDiaryPdfFromCloud({
          patientId,
          date,
          timeOffset,
          timeZone,
          withEncouragements,
          withImages,
        }),
      );
      return result;
    } catch (error: unknown) {
      throw new GenericException(error);
    }
  }

  valueChanges(
    patientId: string,
    diaryId: string,
  ): Observable<DiarySchema | undefined> {
    return this.collection(patientId).doc(diaryId).valueChanges();
  }

  async newDiaryId(patientId: string): Promise<string> {
    const snap = await firstValueFrom(this.collection(patientId).doc().get());
    return snap.id;
  }

  async upload(path: string, file: File, type: string) {
    if (path == null || file == null) {
      throw new GenericException();
    }
    try {
      const fileRef = this.storage.ref(path);

      await this.storage.upload(path, file, {
        contentType: type,
      });

      return Picture.create({
        url: await firstValueFrom(fileRef.getDownloadURL()),
        path: path,
      });
    } catch (error: unknown) {
      throw new GenericException(error);
    }
  }

  removeFile(path: string) {
    try {
      const fileRef = this.storage.ref(path);
      fileRef.delete();
    } catch (error: unknown) {
      throw new GenericException(error);
    }
  }

  async delete(diary: Diary) {
    console.log('❌ diary', diary);

    for (const picture of diary.pictures) {
      this.removeFile(picture.path);
    }
    return this.collection(diary.patientId.id.toString())
      .doc(diary.id.toString())
      .delete();
  }
}
